import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Button,
  FormFeedback,
  CustomInput,
  Spinner,
  Table,
} from "reactstrap";
import { useFormik, Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import usePersistStoreData from "../../../Components/Hooks/usePersistStoreData";
import CommonLoading from "../../../Components/Common/CommonLoading";
import {
  ProfileDetailThunk,
  reKyc_AccountClosureThunk,
  reKyc_Esign_DocumentDownloadThunk,
  reKyc_UpdateOtherDetailsThunk,
} from "../../../slices/thunks";
import { showToast } from "../../../Components/ToastNotification";
import ConfirmationModal from "../../../Components/Modals/ConfirmationModal";
import EditNomineeDetailsModal from "../../../Components/Modals/EditNomineeDetailsModal";
import Message from "../../../Components/Common/Message";

const OtherDetails = ({ setSteps, steps }) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { userData } = usePersistStoreData();
  const token = userData?.token;
  const [loading, setLoading] = useState(false);
  //state of nominee list data
  const [nomineeList, setNomineeList] = useState([]);

  const { reKyc_UpdateOthersDetailData, reKyc_UpdateOthersDetailLoading } =
    useSelector((state) => state.Re_Kyc_Update_Others_Details);

  //states and logic for add nominee

  const FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const SUPPORTED_FORMATS = ["application/pdf", "image/jpeg"];

  const nomineeValidationSchema = Yup.object({
    nominee_name: Yup.string()
      .matches(/^[A-Za-z ]*$/, "Only alphabets are allowed")
      .required("Nominee name is required!"),

    nominee_share: Yup.number()
      .typeError("Share % must be a number")
      .min(1, "Minimum 1% required")
      .max(100, "Cannot exceed 100%")
      .required("Share % is required"),

    nominee_relationship: Yup.string()
      .matches(/^[A-Za-z ]*$/, "Only alphabets are allowed")
      .required("Relationship is required!"),

    nominee_email: Yup.string()
      .trim()
      .email("Enter a valid email")
      .required("Email is required"),

    nominee_mobile: Yup.string()
      .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit mobile number")
      .required("Mobile number is required"),

    nominee_address: Yup.string().trim().required("Address is required"),

    nominee_identity: Yup.string()
      .matches(/^\d{4}$/, "Identity No must be exactly 4 digits")
      .required("Identity No is required"),

    nominee_document: Yup.mixed()
      .required("Nominee document is required")
      .test("fileType", "Only PDF or JPEG files are allowed", (value) => {
        return value && SUPPORTED_FORMATS.includes(value.type);
      })
      .test("fileSize", "File size must be less than 2 MB", (value) => {
        return value && value.size <= FILE_SIZE;
      }),
    is_minor: Yup.string(), // NOT required
    nominee_gaurdian: Yup.string().when("is_minor", {
      is: "1", // when is_minor is "1"
      then: (schema) => schema.required("Guardian name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const validationAddNominee = useFormik({
    enableReinitialize: true, // Ensures Formik updates when initialValues change
    initialValues: {
      nominee_name: "",
      nominee_share: "",
      nominee_relationship: "",
      nominee_email: "",
      nominee_mobile: "",
      nominee_address: "",
      nominee_identity: "",
      nominee_document: "",
      nominee_gaurdian: "",
      nominee_dob: "",
    },
    validationSchema: nomineeValidationSchema,
    onSubmit: (values) => {
      // console.log("Form Submitted", values);
    },
  });

  // states and logic for add nominee table

  const handleAddNominee = async () => {
    const isValid = await validationAddNominee.validateForm();
    validationAddNominee.setTouched({
      nominee_name: true,
      nominee_share: true,
      nominee_relationship: true,
      nominee_email: true,
      nominee_mobile: true,
      nominee_address: true,
      nominee_identity: true,
      nominee_document: true,
    });

    if (Object.keys(isValid).length === 0) {
      if (nomineeList.length >= 10) {
        showToast("You can add a maximum of 10 nominees.");
        return;
      }

      setNomineeList([...nomineeList, validationAddNominee.values]);
      validationAddNominee.resetForm();
      const fileInput = document.getElementById("nominee_document");
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  const columns = [
    { key: "index", label: "S.No." },
    { key: "nominee_name", label: "Name" },
    { key: "nominee_share", label: "Share (%)" },
    { key: "nominee_relationship", label: "Relationship" },
    { key: "nominee_email", label: "Email" },
    { key: "nominee_mobile", label: "Mobile" },
    { key: "nominee_address", label: "Address" },
    { key: "nominee_identity", label: "Identity No" },
    { key: "nominee_document", label: "Document Name" },
    { key: "nominee_gaurdian", label: "Gaurdian" },
    { key: "nominee_dob", label: "DOB" },
    { key: "action", label: "Action" },
  ];

  const [editNomineeData, setEditNomineeData] = useState(null);
  const [editIndex, setEditIndex] = useState(null);

  const handleEditNominee = (index) => {
    setEdit_modal(true);
    setEditIndex(index);
    setEditNomineeData(nomineeList[index]);
  };
  // state for modal to edit nominee details
  const [editModal, setEdit_modal] = useState(false);
  const closeEditModal = () => {
    setEdit_modal(false);
  };

  //handle function to download nominee not opted
  const handleNomineeNotOpted = () => {
    dispatch(
      re_kyc_nomineeNotOptedThunk({
        token,
      })
    )
      .unwrap()
      .then((result) => {
        const blob = new Blob([result], { type: "application/pdf" });

        const fileURL = window.URL.createObjectURL(blob);

        // Open in new tab
        window.open(fileURL, "_blank");

        // Also download
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = "annual_income.pdf"; // file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToast("Downloaded successfully", "success");
      })
      .catch((error) => {
        showToast(
          error?.message || "Something went wrong. Please try again later",
          "error"
        );
      });
  };

  // update other details
  const handleOtherDetails = (values) => {
    if (newSegments?.length === 0) {
      showToast("Please select at least one segment", "error");
      return;
    }
    const formData = new FormData();

    formData.append("new_segments", newSegments);
    formData.append("annual_income", values?.selectedIncome);
    formData.append("occupation", values?.selectedOccupation);
    formData.append("is_politically_exposed", values?.politicallyExposed);
    formData.append("is_other_tax_residence", values?.other_country);

    dispatch(reKyc_UpdateOtherDetailsThunk({ body: formData, token }))
      .then((res) => {
        if (res?.payload?.code === 200) {
          showToast(
            res?.payload?.message || "Other Details Updated Successfully",
            "success"
          );
          setSteps(steps + 1);
        } else {
          showToast(
            res?.payload || "Something went wrong, please try again",
            "error"
          );
        }
      })
      .catch((err) => {
        showToast(
          err?.message || "Something went wrong, please try again",
          "error"
        );
      });
  };

  // options for select income
  const optionIncome = [
    { key: 1, label: "Below 1 lakh" },
    { key: 2, label: "1 - 5 lakhs" },
    { key: 3, label: "5 - 10 lakhs" },
    { key: 4, label: "10 - 25 lakhs" },
    { key: 5, label: "25 lakhs - 1 Crore" },
    { key: 6, label: "More than 1 Crore" },
  ];

  //options for select occupation
  const optionOccupation = [
    { key: 1, label: "Professional" },
    { key: 2, label: "Self Employed" },
    { key: 3, label: "Retired" },
    { key: 4, label: "Housewife" },
    { key: 5, label: "Student" },
    { key: 6, label: "Business" },
    { key: 7, label: "Private/Public Sector" },
    { key: 8, label: "Govt. sector" },
    { key: 9, label: "Agriculture" },
    { key: 10, label: "Others" },
  ];

  const initialSegments = Object.entries(
    userData?.trade?.active_exchanges || {}
  )
    .filter(([_, isActive]) => isActive)
    .map(([_, __], i) => i + 1);

  const [exchangeStates, setExchangeStates] = useState(
    reKyc_UpdateOthersDetailData?.segments ||
      userData?.personal_details?.new_details?.active_exchanges ||
      userData?.trade?.active_exchanges ||
      {}
  );
  const [newSegments, setNewSegments] = useState(initialSegments);

  const exchangeEntries = Object.entries(exchangeStates);

  // validation for other details
  const otherDetailsValidation = Yup.object({
    selectedIncome: Yup.string().required("Annual Income is required"),
    politicallyExposed: Yup.string().required(
      "Please select Politically Exposed status"
    ),
    selectedOccupation: Yup.string().required("Occupation is required"),
    other_country: Yup.string().required("Please select Other Country status"),
    // add_nominee: Yup.string().required("Please select nominee status"),
  });

  const otherDetailsFormik = useFormik({
    initialValues: {
      selectedIncome:
        reKyc_UpdateOthersDetailData?.annual_income ||
        userData?.personal_details?.new_details?.annual_income ||
        userData?.trade?.annual_income_bracket ||
        "",
      politicallyExposed: 0,
        // reKyc_UpdateOthersDetailData?.is_politically_exposed ??
        // userData?.personal_details?.new_details?.pollitically_exposed ??
        // null,
      selectedOccupation:
        reKyc_UpdateOthersDetailData?.occupation ||
        userData?.personal_details?.new_details?.occupation ||
        userData?.trade?.occupation ||
        "",
      other_country:
        reKyc_UpdateOthersDetailData?.is_other_tax_residence ??
        userData?.personal_details?.new_details?.other_tax_residence ??
        null,
      // add_nominee: "",
    },
    validationSchema: otherDetailsValidation,
    onSubmit: (values) => {
      // if (values.add_nominee === "1") {
      //   if (!nomineeList || nomineeList.length === 0) {
      //     showToast("Please add nominee details", "error");
      //     return;
      //   }

      //   const totalNomineeShare = nomineeList?.reduce((sum, item) => {
      //     return sum + Number(item?.nominee_share || 0);
      //   }, 0);

      //   if (totalNomineeShare !== 100) {
      //     showToast("Total nominee share will be 100%", "error");
      //     return;
      //   }
      // }
      handleOtherDetails(values);
    },
  });

  return (
    <>
      <div className="row m-0 p-0">
        {userData?.rekcy_appication_status === "rejected" && (
          <div className=" m-0 p-0 col-12">
            
            <Message
              message={userData?.rekcy_appication_sreason}
              color="danger"
              mb={3}
            />
          </div>
        )}

        <div className=" m-0 p-0 col-xxl-6 col-xl-9 col-lg-8 col-md-8 col-sm-12 col-12">
          <div className="mb-3">
            <span className="badge bg-success p-2 fs-p-sm text-uppercase">
              Trading Segment
            </span>
          </div>
        </div>
      </div>
      <div className="row m-0 p-0">
        <div className="m-0 p-0  mb-3  col-12">
          <div className="d-flex flex-md-row flex-column gap-3 flex-wrap">
            {exchangeEntries?.map(([exchange, isActive], i) => {
              const id = i + 1; // ID based on sequence

              return (
                <div
                  className="pb-2 border-bottom d-flex align-items-center justify-content-between"
                  key={i}
                >
                  <h4 className="text-primary m-0 p-0">{exchange}</h4>

                  <div className="form-check form-switch form-switch-success form-switch-md ms-2">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`exchange-${exchange}`}
                      checked={isActive}
                      disabled={
                        userData?.trade?.active_exchanges?.[exchange] === true
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;

                        setExchangeStates({
                          ...exchangeStates,
                          [exchange]: checked,
                        });

                        setNewSegments(
                          (prev) =>
                            checked
                              ? [...new Set([...prev, id])] // add
                              : prev.filter((item) => item !== id) // remove
                        );
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* FATCA Declaration */}
      <div className="row m-0 p-0">
        <div className=" m-0 p-0 col-xxl-6 col-xl-9 col-lg-8 col-md-8 col-sm-12 col-12">
          <div className="mb-3">
            <span className="badge bg-success p-2 fs-p-sm text-uppercase">
              FATCA Declaration
            </span>
          </div>
        </div>
      </div>
      <Row className="row m-0 p-0">
        <Col lg={6} className="mb-3 ps-0">
          <Label htmlFor="zipcodeInput" className="form-label">
            Select Annual Income
          </Label>
          <Input
            type="select"
            className="form-control"
            name="selectedIncome"
            id="selectedIncome"
            value={otherDetailsFormik.values.selectedIncome}
            onChange={otherDetailsFormik.handleChange}
            onBlur={otherDetailsFormik.handleBlur}
            invalid={
              otherDetailsFormik.touched.selectedIncome &&
              otherDetailsFormik.errors.selectedIncome
            }
          >
            <option value="" disabled>
              Select Income
            </option>
            {optionIncome?.map((item, index) => (
              <option value={item?.key} key={item?.key}>
                {item?.label}
              </option>
            ))}
          </Input>
          <FormFeedback>
            {otherDetailsFormik.errors.selectedIncome}
          </FormFeedback>
        </Col>
        <Col lg={6} className="mb-3 ps-0 ps-lg-3 ">
          <Label htmlFor="zipcodeInput" className="form-label">
            Select Occupation
          </Label>
          <Input
            type="select"
            className="form-control"
            name="selectedOccupation"
            id="selectedOccupation"
            value={
              otherDetailsFormik?.values?.selectedOccupation ??
              userData?.personal_details?.new_details?.occupation
            }
            onChange={otherDetailsFormik.handleChange}
            onBlur={otherDetailsFormik.handleBlur}
            invalid={
              otherDetailsFormik.touched.selectedOccupation &&
              otherDetailsFormik.errors.selectedOccupation
            }
          >
            <option value="" disabled>
              Select Occupation
            </option>
            {optionOccupation?.map((item, index) => (
              <option value={item?.key} key={item?.key}>
                {item?.label}
              </option>
            ))}
          </Input>
          <FormFeedback>
            {otherDetailsFormik.errors.selectedOccupation}
          </FormFeedback>
        </Col>

        <Col lg={6} className="mb-3 ps-0">
          <Label className="form-label" htmlFor="politicallyExposed">
            Politically Exposed
          </Label>

          <div id="politicallyExposed">
            <FormGroup check inline className="">
              <Input
                type="radio"
                name="politicallyExposed"
                id="exposedYes"
                value={1}
                checked={otherDetailsFormik.values.politicallyExposed === 1}
                onChange={(e) =>
                  otherDetailsFormik.setFieldValue(
                    "politicallyExposed",
                    Number(e.target.value)
                  )
                }
                onBlur={otherDetailsFormik.handleBlur}
                disabled
              />
              <Label check htmlFor="exposedYes">
                Yes
              </Label>
            </FormGroup>
            <FormGroup check inline className="ms-3">
              <Input
                type="radio"
                name="politicallyExposed"
                id="exposedNo"
                value={0}
                checked={otherDetailsFormik.values.politicallyExposed === 0}
                onChange={(e) =>
                  otherDetailsFormik.setFieldValue(
                    "politicallyExposed",
                    Number(e.target.value)
                  )
                }
                onBlur={otherDetailsFormik.handleBlur}
                disabled
              />
              <Label check htmlFor="exposedNo">
                No
              </Label>
            </FormGroup>
            {otherDetailsFormik.touched.politicallyExposed &&
              otherDetailsFormik.errors.politicallyExposed && (
                <FormFeedback className="d-block">
                  {otherDetailsFormik.errors.politicallyExposed}
                </FormFeedback>
              )}
          </div>
        </Col>

        <Col lg={6} className="mb-3 ps-0 ps-lg-3">
          <Label className="form-label" htmlFor="other_country">
            Do you have any other country for tax residence ?
          </Label>

          <div id="other_country">
            <FormGroup check inline className="">
              <Input
                type="radio"
                name="other_country"
                id="otherYes"
                value={1}
                checked={otherDetailsFormik.values.other_country === 1}
                onChange={(e) =>
                  otherDetailsFormik.setFieldValue(
                    "other_country",
                    Number(e.target.value)
                  )
                }
                onBlur={otherDetailsFormik.handleBlur}
              />
              <Label check htmlFor="otherYes">
                Yes
              </Label>
            </FormGroup>
            <FormGroup check inline className="ms-3">
              <Input
                type="radio"
                name="other_country"
                id="otherNo"
                value={0}
                checked={otherDetailsFormik.values.other_country === 0}
                onChange={(e) =>
                  otherDetailsFormik.setFieldValue(
                    "other_country",
                    Number(e.target.value)
                  )
                }
                onBlur={otherDetailsFormik.handleBlur}
              />
              <Label check htmlFor="otherNo">
                No
              </Label>
            </FormGroup>
            {otherDetailsFormik.touched.other_country &&
              otherDetailsFormik.errors.other_country && (
                <FormFeedback className="d-block">
                  {otherDetailsFormik.errors.other_country}
                </FormFeedback>
              )}
          </div>
        </Col>
        <Col lg={12}>
          <div className="hstack gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-soft-secondary"
              onClick={() => {
                setSteps(steps - 1);
              }}
            >
              Prev
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={otherDetailsFormik.handleSubmit}
              disabled={reKyc_UpdateOthersDetailLoading}
              style={{
                minWidth: "60px",
              }}
            >
              {reKyc_UpdateOthersDetailLoading ? <Spinner size="sm" /> : "Next"}
            </button>
          </div>
        </Col>
      </Row>
      {/* <Row className="row m-0 p-0">
      <Col lg={6} className="mb-3 ps-0">
        <Label
          className="form-label"
          htmlFor="add_nominee"
        >
          Do you want to add Nominee ?
        </Label>

        <div id="add_nominee">
          <FormGroup check inline className="">
            <Input
              type="radio"
              name="add_nominee"
              id="otherYes"
              value="1"
              checked={
                otherDetailsFormik.values
                  .add_nominee === "1"
              }
              onChange={
                otherDetailsFormik.handleChange
              }
              onBlur={otherDetailsFormik.handleBlur}
            />
            <Label check htmlFor="otherYes">
              Yes
            </Label>
          </FormGroup>
          <FormGroup check inline className="ms-3">
            <Input
              type="radio"
              name="add_nominee"
              id="otherNo"
              value="0"
              checked={
                otherDetailsFormik.values
                  .add_nominee === "0"
              }
              onChange={
                otherDetailsFormik.handleChange
              }
              onBlur={otherDetailsFormik.handleBlur}
            />
            <Label check htmlFor="otherNo">
              No
            </Label>
          </FormGroup>
          {otherDetailsFormik.touched.add_nominee &&
            otherDetailsFormik.errors.add_nominee && (
              <FormFeedback className="d-block">
                {
                  otherDetailsFormik.errors
                    .add_nominee
                }
              </FormFeedback>
            )}
        </div>
      </Col>

      {otherDetailsFormik.values.add_nominee ===
      "1" ? (
        <>
          <NomineeFormFields
            formik={validationAddNominee}
            nomineeList={nomineeList}
            handleAddNominee={handleAddNominee}
            columns={columns}
            handleEditNominee={handleEditNominee}
            setNomineeList={setNomineeList}
            editIndex={editIndex}
            setEditIndex={setEditIndex}
          />
        </>
      ) : otherDetailsFormik.values.add_nominee ===
        "0" ? (
        <DocumentActionCard
          text="Nominee not opted document"
          iconType="download"
          onClick={() => {
            handleNomineeNotOpted();
          }}
          supportText="You have not opted the nominee, 
          so please download the document, sign it 
          manually, and submit it to the head office."
          loading={re_kyc_nomineeNotOptedLoading}
        />
      ) : null}

      <Col lg={12}>
        <div className="hstack gap-2 justify-content-end">
          <button
            type="button"
            className="btn btn-soft-secondary"
            onClick={() => {
              setSteps(steps - 1);
            }}
          >
            Prev
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={otherDetailsFormik.handleSubmit}
          >
            Next
          </button>
        </div>
      </Col>
    </Row> */}
      {editModal && (
        <EditNomineeDetailsModal
          editModal={editModal}
          closeEditModal={closeEditModal}
          editNomineeData={editNomineeData}
          editIndex={editIndex}
          nomineeList={nomineeList}
          setNomineeList={setNomineeList}
          validationAddNominee={nomineeValidationSchema}
        />
      )}
    </>
  );
};

export default OtherDetails;
